import React, { useEffect, useRef, useState } from 'react';

import { BrowseResultItem, FromStore } from '@cartwhl/common/types';
import LazyVideo from '@cartwhl/ui/atoms/LazyVideo';
import LoadingBlock from '@cartwhl/ui/atoms/LoadingBlock';
import Text from '@cartwhl/ui/base/Text';
import useGrabToScroll from '@cartwhl/ui/hooks/useGrabScroll';
import PlusIcon from '@cartwhl/ui/icons/PlusIcon';
import Button from '@cartwhl/ui/input/Button';

import { BROWSE_DESKTOP_MIN_WIDTH } from 'components/BrowseMotions/constants';
import useBreakpoint from 'hooks/useBreakpoint';
import { animationGenerationStore, posingStore } from 'stores';
import type { PoseSuggestion } from 'stores/browsePoseStore';

import { useLibraryStore } from '../useLibraryStore';
import BrowseGif from './BrowseGif';
import BrowseHeartButton from './BrowseHeartButton';
import BrowseMotionMoreMenu from './BrowseSearchPreviewMoreMenu';

type PreviewItem = BrowseResultItem | PoseSuggestion;

type Suggestions = {
  searchTerm: string;
  data: PreviewItem[];
  loading: boolean;
  total: number;
};

export default function BrowseSearchPreview({
  suggestions,
  highlightedSuggestionID,
}: {
  suggestions: FromStore<Suggestions>;
  highlightedSuggestionID: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());

  useGrabToScroll(containerRef);

  // Scroll highlighted item into view with padding
  useEffect(() => {
    if (
      highlightedSuggestionID &&
      itemRefsMap.current.has(highlightedSuggestionID) &&
      containerRef.current
    ) {
      const itemElement = itemRefsMap.current.get(highlightedSuggestionID);

      const container = containerRef.current;

      if (itemElement) {
        const itemRect = itemElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const scrollPadding = 8; // Extra padding in pixels

        // Calculate if item is out of view (left or right)
        const isLeftOutOfView = itemRect.left < containerRect.left;
        const isRightOutOfView = itemRect.right > containerRect.right;

        if (isLeftOutOfView) {
          // Scroll left with padding
          const scrollAmount =
            itemRect.left - containerRect.left - scrollPadding;
          container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        } else if (isRightOutOfView) {
          // Scroll right with padding
          const scrollAmount =
            itemRect.right - containerRect.right + scrollPadding;
          container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
      }
    }
  }, [highlightedSuggestionID]);

  return (
    <div>
      <Text as="h2" variant="studio/body/medium/sm" className="px-4 pt-4">
        {suggestions.searchTerm.length > 0
          ? `Suggestions for “${suggestions.searchTerm}”`
          : 'Suggestions'}
      </Text>
      <div
        ref={containerRef}
        className="styled-scrollbar flex h-[202px] gap-x-2 overflow-x-auto px-4 pt-3 pb-1"
      >
        {suggestions.loading && (
          <>
            <LoadingItem />
            <LoadingItem />
            <LoadingItem />
            <LoadingItem />
            <LoadingItem />
          </>
        )}
        {suggestions.data.map((item) => {
          if (isPoseSuggestion(item)) {
            return (
              <PoseSuggestionItem
                key={item.jobID}
                item={item}
                isHighlighted={item.jobID === highlightedSuggestionID}
                ref={(el) => {
                  if (el) {
                    itemRefsMap.current.set(item.jobID, el);
                  } else {
                    itemRefsMap.current.delete(item.jobID);
                  }
                }}
              />
            );
          }
          if (isBrowseResultItem(item)) {
            return (
              <AnimationSuggestionItem
                key={item.jobID}
                item={item}
                isHighlighted={item.jobID === highlightedSuggestionID}
                ref={(el) => {
                  if (el) {
                    itemRefsMap.current.set(item.jobID, el);
                  } else {
                    itemRefsMap.current.delete(item.jobID);
                  }
                }}
              />
            );
          }
        })}
        {!suggestions.loading &&
          suggestions.data.length === 0 &&
          suggestions.searchTerm.length > 0 && (
            <div className="flex h-full w-full flex-none items-center justify-center text-center">
              <Text variant="studio/body/sm" className="text-gray-60">
                Our search didn’t pull up any accurate results.
                {/* <br />
                <button onClick={}>Browse the full library for more options</button> */}
              </Text>
            </div>
          )}
      </div>
    </div>
  );
}

const AnimationSuggestionItem = React.forwardRef<
  HTMLDivElement,
  {
    item: BrowseResultItem;
    isHighlighted: boolean;
  }
>(({ item, isHighlighted }, ref) => {
  const { isMobile } = useBreakpoint(BROWSE_DESKTOP_MIN_WIDTH);

  const { favorites } = useLibraryStore();

  async function handleAddMotion(motion: BrowseResultItem) {
    await animationGenerationStore.submitJobFromExistingMotion({
      currentAnimationNumber:
        animationGenerationStore.state.currentAnimationNumber,
      existingMotionAnimationNumber: 0,
      existingMotionJobID: motion.jobID,
    });
  }

  return (
    <div
      ref={ref}
      className="flex shrink-0 flex-col"
      onClick={() => {
        if (isMobile) {
          handleAddMotion(item);
        }
      }}
    >
      <div
        className={`group relative flex ${isMobile ? 'cursor-pointer' : ''}`}
      >
        {!isMobile && (
          <div className="pointer-events-none absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 justify-center">
            <div
              className={`pointer-events-auto opacity-0 transition-opacity group-hover:opacity-100 ${isHighlighted ? 'opacity-100' : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant="primary"
                size="sm"
                icon={<PlusIcon />}
                onClick={() => handleAddMotion(item)}
              >
                Add Animation
              </Button>
            </div>
          </div>
        )}

        {favorites.has(item.jobID) && (
          <div className="pointer-events-none absolute top-2 left-2 z-10 flex justify-end">
            <div
              className="pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <BrowseHeartButton jobID={item.jobID} title={item.title} />
            </div>
          </div>
        )}
        <div
          className={`pointer-events-auto absolute top-2 right-2 z-10 hidden justify-end opacity-0 transition-opacity group-hover:opacity-100 data-[state=open]:opacity-100 md:flex`}
          onClick={(e) => e.stopPropagation()}
        >
          <BrowseMotionMoreMenu motion={item as BrowseResultItem} />
        </div>

        {item.gifPathV2 ? (
          <div className="w-[150px]">
            <BrowseGif
              src={item.gifPathV2}
              contained={false}
              isHighlighted={isHighlighted}
            />
          </div>
        ) : (
          <LazyVideo
            className="bg-gray-15 dark:bg-gray-75 aspect-square w-[150px] rounded-lg"
            src={item.mp4PathV2}
            fallbackSrcs={[item.mp4Path]}
            mode="autoplay"
            contained={false}
          />
        )}
      </div>
      <Text
        className="text-gray-60 mt-2 ml-1 max-w-[140px] overflow-hidden text-ellipsis whitespace-nowrap"
        variant="studio/body/sm"
      >
        {item.title}
      </Text>
    </div>
  );
});
AnimationSuggestionItem.displayName = 'AnimationSuggestionItem';

const PoseSuggestionItem = React.forwardRef<
  HTMLDivElement,
  {
    item: PoseSuggestion;
    isHighlighted: boolean;
  }
>(({ item, isHighlighted }, ref) => {
  const { isMobile } = useBreakpoint(BROWSE_DESKTOP_MIN_WIDTH);
  const [isAddingPose, setIsAddingPose] = useState(false);

  async function handleAddPose(pose: PoseSuggestion) {
    setIsAddingPose(true);
    await posingStore.loadPoseFromJobID(pose.jobID);
    setIsAddingPose(false);
  }

  return (
    <div
      ref={ref}
      className="flex shrink-0 flex-col"
      onClick={() => {
        if (isMobile) {
          handleAddPose(item);
        }
      }}
    >
      <div
        className={`group relative flex ${isMobile ? 'cursor-pointer' : ''}`}
      >
        {!isMobile && (
          <div className="pointer-events-none absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 justify-center">
            <div
              className={`pointer-events-auto opacity-0 transition-opacity group-hover:opacity-100 ${isHighlighted ? 'opacity-100' : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                loading={isAddingPose}
                variant="primary"
                size="sm"
                icon={<PlusIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddPose(item);
                }}
              >
                Add pose
              </Button>
            </div>
          </div>
        )}
        <div
          className={`bg-gray-15 dark:bg-gray-75 flex aspect-square w-[150px] items-center justify-center rounded-lg ${isHighlighted ? 'ring-blue ring-4' : ''}`}
        >
          {item.thumbnailUrl ? (
            <img
              src={item.thumbnailUrl}
              alt={item.title}
              className="aspect-square w-[150px] rounded-lg object-cover"
            />
          ) : (
            <Text variant="studio/body/sm">No Image</Text>
          )}
        </div>
      </div>
      <Text
        className="text-gray-60 mt-2 ml-1 max-w-[140px] overflow-hidden text-ellipsis whitespace-nowrap"
        variant="studio/body/sm"
      >
        {item.title}
      </Text>
    </div>
  );
});

PoseSuggestionItem.displayName = 'PoseSuggestionItem';

function LoadingItem() {
  return (
    <div className="flex flex-col gap-2">
      <LoadingBlock color="gray" className="h-[150px] w-[150px] rounded-lg" />
      <LoadingBlock color="gray" className="h-[20px] w-[80px]" />
    </div>
  );
}

function isBrowseResultItem(item: PreviewItem): item is BrowseResultItem {
  // BrowseResultItem has properties like mp4Path or gifPathV2
  return (
    (item as BrowseResultItem).mp4Path !== undefined ||
    (item as BrowseResultItem).mp4PathV2 !== undefined ||
    (item as BrowseResultItem).gifPathV2 !== undefined
  );
}

function isPoseSuggestion(item: PreviewItem): item is PoseSuggestion {
  return (item as PoseSuggestion).thumbnailUrl !== undefined;
}
